import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { Btn } from '../../components/Btn';
import { EmptyState } from '../../components/EmptyState';
import { KpiCard } from '../../components/KpiCard';
import { Pill } from '../../components/Pill';
import { DataTable } from '../../components/Table';
import { listUsers } from '../../data/fetchers';
import { canEditSystemConfig } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import type { User } from '../../types/domain';
import { SettingsHeader } from './SettingsHeader';
import { UserFormModal } from './UserFormModal';

const ROLE_TH: Record<User['role'], string> = {
  owner: 'เจ้าของ',
  manager: 'ผู้จัดการ',
  cashier: 'แคชเชียร์',
};

export function UsersView() {
  const user = useSession((s) => s.user);
  const canEdit = user ? canEditSystemConfig(user) : false;
  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: listUsers,
  });
  const [editing, setEditing] = useState<User | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const activeCount = users.filter((item) => item.active).length;
  const managerCount = users.filter((item) => item.role === 'manager').length;
  const cashierCount = users.filter((item) => item.role === 'cashier').length;

  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (u: User) => {
    setEditing(u);
    setModalOpen(true);
  };

  const columns = useMemo<ColumnDef<User, unknown>[]>(
    () => [
      { accessorKey: 'name', header: 'ชื่อ' },
      { accessorKey: 'phone', header: 'เบอร์โทร' },
      {
        accessorKey: 'role',
        header: 'บทบาท',
        cell: (c) => ROLE_TH[c.getValue<User['role']>()],
      },
      {
        id: 'branches',
        header: 'สาขา',
        cell: (c) => c.row.original.branchIds.length || 'ทั้งหมด',
      },
      {
        accessorKey: 'active',
        header: 'สถานะ',
        cell: (c) =>
          c.getValue<boolean>() ? (
            <Pill tone="ok">ใช้งาน</Pill>
          ) : (
            <Pill tone="neutral">ปิด</Pill>
          ),
      },
      {
        id: 'pin',
        header: 'PIN',
        cell: () => <span className="num text-ink-3">••••</span>,
      },
      {
        id: 'go',
        header: '',
        cell: (c) => (
          <button
            aria-label={`แก้ไขผู้ใช้ ${c.row.original.name}`}
            className="font-bold text-primary text-sm-label hover:underline"
            onClick={() => openEdit(c.row.original)}
          >
            แก้ไข
          </button>
        ),
      },
    ],
    [],
  );

  if (!canEdit) {
    return (
      <div className="bo-fade">
        <SettingsHeader
          title="ผู้ใช้และสิทธิ์"
          subtitle="กำหนดบทบาท สาขาที่เข้าถึง และสถานะการใช้งานของทีม"
          status={{ label: 'คุณไม่มีสิทธิ์แก้ไข', tone: 'neutral' }}
        />
        <div className="bo-card">
          <EmptyState
            title="เฉพาะเจ้าของ"
            sub="ผู้ใช้และสิทธิ์จัดการได้โดยเจ้าของหรือผู้ที่ได้รับสิทธิ์ตั้งค่าระบบ"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bo-fade">
      <SettingsHeader
        title="ผู้ใช้และสิทธิ์"
        subtitle="กำหนดบทบาท สาขาที่เข้าถึง และสถานะการใช้งานของทีม"
        status={{ label: `${activeCount} คนกำลังใช้งาน`, tone: 'ok' }}
        actions={
          <Btn kind="primary" onClick={openAdd}>
            + เพิ่มผู้ใช้
          </Btn>
        }
      />
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <KpiCard label="ผู้ใช้ทั้งหมด" value={users.length} sub="ทุกบทบาท" />
        <KpiCard
          label="ผู้จัดการ"
          value={managerCount}
          sub="ดูแลสาขาและข้อมูลหลัก"
          tone="accent"
        />
        <KpiCard
          label="แคชเชียร์"
          value={cashierCount}
          sub="เข้าใช้งานเครื่อง POS"
          tone="ok"
        />
      </div>
      <DataTable columns={columns} data={users} />
      <UserFormModal
        open={modalOpen}
        user={editing}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}
